import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, ChevronRight, CheckCircle, Clock, Star, BookOpen } from "lucide-react";
import { usePageMeta } from "./usePageMeta";
import { CityFAQ, faqSchema } from "./CityFAQ";

const SITE = "https://kennethcarsoninsurance.com";
const COMPARION_URL = "https://www.comparioninsurance.com/insurance-agent/california/elk-grove-0488/kenneth-carson";

const agencySchema = (city, r) => ({
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  "@id": `${SITE}${r.basePath}/${city.slug}#agency`,
  "parentOrganization": { "@id": `${SITE}/#organization` },
  "name": `Kenneth Carson - Life Insurance Agent serving ${city.name}, ${r.abbr}`,
  "url": `${SITE}${r.basePath}/${city.slug}`,
  "image": city.image,
  "telephone": "+1-916-505-1000",
  "email": "Kenneth.Carson@Comparion.com",
  "sameAs": [COMPARION_URL],
  "address": { "@type": "PostalAddress", "streetAddress": "9381 E Stockton Blvd, Suite 120", "addressLocality": "Elk Grove", "addressRegion": "CA", "postalCode": "95624", "addressCountry": "US" },
  "areaServed": [
    { "@type": "City", "name": `${city.name}, ${r.stateName}`, "geo": { "@type": "GeoCoordinates", ...city.geo } },
    ...city.neighborhoods.map((n) => ({ "@type": "Place", "name": `${n}, ${r.abbr}` })),
    { "@type": "State", "name": r.stateName }
  ],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "74" },
  "makesOffer": ["Term Life Insurance", "Whole Life Insurance", "Universal Life Insurance"].map((n) => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": `${n} in ${city.name}, ${r.abbr}` } }))
});

const breadcrumbSchema = (city, r) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
    { "@type": "ListItem", "position": 2, "name": r.stateName, "item": `${SITE}${r.basePath}` },
    { "@type": "ListItem", "position": 3, "name": city.name, "item": `${SITE}${r.basePath}/${city.slug}` }
  ]
});

const CityHero = ({ city, r }) => (
  <section className="relative min-h-[560px] flex items-end overflow-hidden">
    <img src={city.image} alt={`${city.name}, ${city.area}, ${r.stateName}`} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#143628] via-[#143628]/70 to-[#143628]/20" />
    <div className="relative max-w-7xl w-full mx-auto px-6 sm:px-8 lg:px-12 pb-16 pt-32">
      <nav className="font-manrope text-sm text-white/70 mb-6" data-testid={`${r.key}-breadcrumb`}>
        <Link to="/" className="hover:text-white">Home</Link> / <Link to={r.basePath} className="hover:text-white" data-testid={`breadcrumb-${r.key}`}>{r.stateName}</Link> / <span className="text-white">{city.name}</span>
      </nav>
      <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-sm font-manrope mb-5">
        <MapPin className="w-4 h-4" /> {city.area} · Licensed in {r.stateName}
      </span>
      <h1 className="font-outfit font-[850] text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl" data-testid={`${r.key}-city-h1`}>{city.headline}</h1>
      <div className="flex items-center gap-2 mt-6 text-white/90 font-manrope">
        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#E8B04B] text-[#E8B04B]" />)}
        <span className="ml-1">5.0 · 74 verified reviews</span>
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-8">
        <a href="#contact" className="inline-flex items-center justify-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-[#A84D37] transition-colors duration-300" data-testid={`${r.key}-city-quote-btn`}>
          Get a Free {city.name} Quote <ChevronRight className="w-5 h-5" />
        </a>
        <a href="tel:+19165051000" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-white/20 transition-colors duration-300" data-testid={`${r.key}-city-call-btn`}>
          <Phone className="w-5 h-5" /> (916) 505-1000
        </a>
        <a href={COMPARION_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-white text-[#143628] px-8 py-4 rounded-full font-manrope font-semibold hover:bg-[#F0B8A8] transition-colors duration-300" data-testid={`${r.key}-city-comparion-hero-btn`}>
          Get My Quote on Comparion <ChevronRight className="w-5 h-5" />
        </a>
      </div>
    </div>
  </section>
);

const CityIntro = ({ city, r }) => (
  <section className="py-20 bg-[#F7F7F5]">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-12">
      <div className="lg:col-span-3">
        <p className="font-manrope text-lg text-[#4A6359] leading-relaxed" data-testid={`${r.key}-city-intro`}>{city.intro}</p>
        <h2 className="font-outfit font-bold text-2xl text-[#143628] mt-12 mb-6">What {city.name} families usually need</h2>
        <div className="space-y-4">
          {city.localNeeds.map((n, i) => (
            <div key={i} className="flex gap-4 bg-white rounded-2xl p-5 border border-[#143628]/5">
              <CheckCircle className="w-6 h-6 text-[#C45A41] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-outfit font-bold text-[#143628]">{n.title}</h3>
                <p className="font-manrope text-[#4A6359] text-sm mt-1">{n.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <aside className="lg:col-span-2 space-y-6">
        <div className="bg-[#143628] rounded-3xl p-8 text-white">
          <h2 className="font-outfit font-bold text-xl mb-4">Areas served near {city.name}</h2>
          <div className="flex flex-wrap gap-2" data-testid={`${r.key}-city-neighborhoods`}>
            {city.neighborhoods.map((n) => <span key={n} className="bg-white/10 px-3 py-1 rounded-full text-sm font-manrope">{n}</span>)}
          </div>
        </div>
        <div className="bg-white rounded-3xl p-8 border border-[#143628]/10">
          <div className="flex items-center gap-2 text-[#143628] font-outfit font-bold mb-3"><Clock className="w-5 h-5 text-[#C45A41]" /> {r.hoursLabel}</div>
          <p className="font-manrope text-sm text-[#4A6359] leading-relaxed">{r.hoursNote}</p>
          <a href={COMPARION_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C45A41] font-manrope text-sm font-semibold mt-4 hover:underline" data-testid={`${r.key}-city-comparion-link`}>
            View Kenneth's Comparion Profile <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </aside>
    </div>
  </section>
);

const CityLinks = ({ city, r }) => (
  <section className="py-20 bg-[#F7F7F5]">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-2 gap-12">
      <div>
        <h2 className="font-outfit font-bold text-2xl text-[#143628] mb-6 flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#C45A41]" /> {r.stateName} life insurance guides</h2>
        <div className="space-y-3">
          {r.relatedPosts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="block font-manrope text-[#4A6359] hover:text-[#C45A41] transition-colors" data-testid={`${r.key}-related-${p.slug}`}>{p.title} →</Link>
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-outfit font-bold text-2xl text-[#143628] mb-6">Other {r.stateName} areas served</h2>
        <div className="flex flex-wrap gap-3">
          {r.cities.filter((c) => c.slug !== city.slug).map((c) => (
            <Link key={c.slug} to={`${r.basePath}/${c.slug}`} className="bg-white border border-[#143628]/10 px-5 py-2.5 rounded-full font-manrope text-[#143628] hover:bg-[#143628] hover:text-white transition-colors duration-300" data-testid={`${r.key}-other-${c.slug}`}>
              {c.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CityPage = ({ region: r, renderContactForm }) => {
  const { city: slug } = useParams();
  const city = r.cities.find((c) => c.slug === slug);
  usePageMeta(city?.metaTitle || r.hubMetaTitle, city?.metaDescription || r.hubMetaDescription);

  if (!city) return <Navigate to={r.basePath} replace />;

  return (
    <div data-testid={`${r.key}-city-page-${city.slug}`}>
      <script type="application/ld+json">{JSON.stringify(agencySchema(city, r))}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema(city.faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(city, r))}</script>
      <CityHero city={city} r={r} />
      <CityIntro city={city} r={r} />
      <CityFAQ faqs={city.faqs} title={`Life Insurance in ${city.name} — FAQs`} prefix={r.key} />
      <CityLinks city={city} r={r} />
      {renderContactForm(`${city.name}, ${r.stateName}`)}
    </div>
  );
};

export default CityPage;
